import { DeviceKind } from '~/generated/graphql';

/**
 * What a device can do, in three tiers:
 *
 * 1. Kind facts — true of every device of a kind, so they live in the static table below.
 *    Centralized so consumers (popover gating, the commands grid, future surfaces) all agree
 *    — no scattered `device.kind === Samsung` conditionals.
 * 2. Server-computed device facts — stable properties of a *device*, not its kind, because they
 *    depend on per-device configuration. These are resolved in Ruby and read off the device (see
 *    `Device#supports_volume?` / `#supports_mute?`, which fold in the Linux `tvControl` property).
 *    Never re-derive them here; that rule has one home and it is the server.
 * 3. Runtime state (e.g. "tune channel needs a channel guide", "update software needs
 *    canUpdateSoftware") stays inline at the call site, since it changes as the device does.
 */
export type KindCapabilities = {
  /** Set Volume — opens the VolumeDialog with an absolute 0-100 level. Samsung & WebOS only. */
  absoluteVolume: boolean;
  /** Set Brightness — opens the BrightnessDialog. Samsung only. */
  brightness: boolean;
  /** Analyze Channels — diagnostics command. Linux only. */
  analyzeChannels: boolean;
  /** Install Firmware — firmware update dialog. Samsung & WebOS only. */
  installFirmware: boolean;
  /** Lock/unlock physical keys on the display. Samsung & WebOS only. */
  physicalKeys: boolean;
  /** Lock/unlock the IR remote. Samsung & WebOS only. */
  remote: boolean;
};

/** Kind facts plus the per-device facts the server computes. */
export type DeviceCapabilities = KindCapabilities & {
  /** Relative volume stepping — the remote's +/- keys. See `Device#supports_volume?`. */
  relativeVolume: boolean;
  /**
   * Mute / unmute. Narrower than relativeVolume: a CEC device steps volume on any player but only
   * mutes on one new enough to implement it. Always a discrete pair of commands: the player's
   * `setMuted` takes an absolute boolean rather than toggling, so there is no single key that
   * could stand in for both.
   */
  mute: boolean;
};

/** The device fields {@link deviceCapabilitiesFor} needs. Structural, so any fragment fits. */
type CapabilityDevice = {
  kind: DeviceKind;
  supportsMute: boolean;
  supportsVolume: boolean;
};

export const kindCapabilities: Record<DeviceKind, KindCapabilities> = {
  [DeviceKind.Android]: {
    absoluteVolume: false,
    brightness: false,
    analyzeChannels: false,
    installFirmware: false,
    physicalKeys: false,
    remote: false,
  },
  [DeviceKind.Brightsign]: {
    absoluteVolume: false,
    brightness: false,
    analyzeChannels: false,
    installFirmware: false,
    physicalKeys: false,
    remote: false,
  },
  [DeviceKind.Linux]: {
    absoluteVolume: false,
    brightness: false,
    analyzeChannels: true,
    installFirmware: false,
    physicalKeys: false,
    remote: false,
  },
  [DeviceKind.Samsung]: {
    absoluteVolume: true,
    brightness: true,
    analyzeChannels: false,
    installFirmware: true,
    physicalKeys: true,
    remote: true,
  },
  [DeviceKind.Web]: {
    absoluteVolume: false,
    brightness: false,
    analyzeChannels: false,
    installFirmware: false,
    physicalKeys: false,
    remote: false,
  },
  [DeviceKind.Webos]: {
    absoluteVolume: true,
    brightness: false,
    analyzeChannels: false,
    installFirmware: true,
    physicalKeys: true,
    remote: true,
  },
};

/** Convenience: kind-only capabilities, for surfaces that don't query the per-device fields. */
export const capabilitiesFor = (kind: DeviceKind): KindCapabilities => kindCapabilities[kind];

/** Full capabilities for a device. Requires `supportsMute` / `supportsVolume` in the query. */
export const deviceCapabilitiesFor = (device: CapabilityDevice): DeviceCapabilities => ({
  ...kindCapabilities[device.kind],
  mute: device.supportsMute,
  relativeVolume: device.supportsVolume,
});
